import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

export interface AvailableLanguage {
  code: string;
  label: string;
}

const LANGUAGE_STORAGE_KEY = 'smc_language';

const AVAILABLE_LANGUAGES: AvailableLanguage[] = [
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
  { code: 'es', label: 'ES' }
];

@Injectable()
export class LanguageService {

  private currentLanguageSubject = new BehaviorSubject<string>('en');
  public currentLanguage$: Observable<string> = this.currentLanguageSubject.asObservable();
  private salonDefaultLanguage = 'en';

  constructor(
    private translate: TranslateService,
    private router: Router
  ) {
    this.translate.addLangs(AVAILABLE_LANGUAGES.map(l => l.code));
    this.translate.setDefaultLang('en');
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.applyUrlLanguageWhenAllowed());
  }

  /**
   * Resolves language using the fallback chain:
   * localStorage → URL query param → salon default → 'en'
   */
  initialize(salonDefaultLanguage?: string): void {
    this.salonDefaultLanguage = this.normalizeLanguage(salonDefaultLanguage) || 'en';
    const resolved = this.resolveLanguage(this.salonDefaultLanguage);
    this.applyLanguage(resolved);
  }

  setLanguage(langCode: string): void {
    if (!AVAILABLE_LANGUAGES.some(l => l.code === langCode)) {
      return;
    }
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, langCode);
    } catch (_) {}
    this.applyLanguage(langCode);
  }

  getAvailableLanguages(): AvailableLanguage[] {
    return AVAILABLE_LANGUAGES;
  }

  getCurrentLanguage(): string {
    return this.currentLanguageSubject.value;
  }

  private applyLanguage(langCode: string): void {
    this.translate.use(langCode);
    this.currentLanguageSubject.next(langCode);
  }

  private resolveLanguage(salonDefault?: string): string {
    const stored = this.getStoredLanguage();
    if (stored) return stored;

    const urlParam = this.getUrlParamLanguage();
    if (urlParam) return urlParam;

    const normalizedSalonDefault = this.normalizeLanguage(salonDefault);
    if (normalizedSalonDefault) {
      return normalizedSalonDefault;
    }

    return 'en';
  }

  private getStoredLanguage(): string | null {
    try {
      const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (stored && AVAILABLE_LANGUAGES.some(l => l.code === stored)) {
        return stored;
      }
    } catch (_) {}
    return null;
  }

  private getUrlParamLanguage(): string | null {
    const params = this.router.parseUrl(this.router.url).queryParams;
    return this.normalizeLanguage(params['lang']);
  }

  private normalizeLanguage(langCode: string | undefined | null): string | null {
    if (!langCode) {
      return null;
    }
    const normalized = langCode.toLowerCase();
    if (AVAILABLE_LANGUAGES.some(l => l.code === normalized)) {
      return normalized;
    }
    return null;
  }

  private applyUrlLanguageWhenAllowed(): void {
    const stored = this.getStoredLanguage();
    if (stored) {
      return;
    }
    const urlLang = this.getUrlParamLanguage();
    if (urlLang && urlLang !== this.getCurrentLanguage()) {
      this.applyLanguage(urlLang);
      return;
    }
    if (!urlLang && this.getCurrentLanguage() !== this.salonDefaultLanguage) {
      this.applyLanguage(this.salonDefaultLanguage);
    }
  }
}
